/** Parsed OKLCH triplet (lightness 0..1, chroma ≥0, hue 0..360). */
export interface OklchTriplet {
  c: number;
  h: number;
  l: number;
}

interface IHexOklch {
  hexToOklch(hex: string): OklchTriplet | null;
  isValidHex(value: string): boolean;
  oklchToHex(oklch: OklchTriplet): string;
}

const HEX_REGEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Convert between sRGB hex (`#rrggbb`) and OKLCH triplets used by the theme studio.
 * Implements the standard sRGB → linear → XYZ-D65 → OKLab → OKLCH pipeline (and its inverse)
 * so the picker can move between user-facing HEX strings and palette OKLCH values.
 */
export const HexOklch: IHexOklch = {
  /**
   * Convert a hex color (3- or 6-digit, optional leading `#`) to OKLCH.
   * @param hex Hex color string.
   * @returns OKLCH triplet, or null when the input cannot be parsed.
   */
  hexToOklch(hex: string): OklchTriplet | null {
    const rgb = _hexToRgb(hex);
    if (!rgb) return null;
    const [lr, lg, lb] = [_srgbToLinear(rgb[0]), _srgbToLinear(rgb[1]), _srgbToLinear(rgb[2])];
    const lab = _linearRgbToOklab(lr, lg, lb);
    return _oklabToOklch(lab);
  },
  /**
   * Test whether a string is a syntactically valid hex color (3- or 6-digit, optional `#`).
   * @param value Candidate string.
   * @returns True if parseable.
   */
  isValidHex(value: string): boolean {
    return HEX_REGEX.test(value.trim());
  },
  /**
   * Convert an OKLCH triplet to a 6-digit lowercase `#rrggbb` hex string.
   * Out-of-gamut colors are clamped to the sRGB cube; this is acceptable for the
   * theme studio's preview swatches where exact roundtrips are not required.
   * @param oklch OKLCH triplet.
   * @returns Hex string in `#rrggbb` form.
   */
  oklchToHex(oklch: OklchTriplet): string {
    const lab = _oklchToOklab(oklch);
    const [lr, lg, lb] = _oklabToLinearRgb(lab);
    const r = _linearToSrgb(lr);
    const g = _linearToSrgb(lg);
    const b = _linearToSrgb(lb);
    return _rgbToHex([r, g, b]);
  }
};

const _hexToRgb = (hex: string): [number, number, number] | null => {
  const trimmed = hex.trim();
  const match = HEX_REGEX.exec(trimmed);
  if (!match) return null;
  const body = match[1];
  const expanded =
    body.length === 3
      ? body
          .split('')
          .map(ch => ch + ch)
          .join('')
      : body;
  const r = Number.parseInt(expanded.slice(0, 2), 16) / 255;
  const g = Number.parseInt(expanded.slice(2, 4), 16) / 255;
  const b = Number.parseInt(expanded.slice(4, 6), 16) / 255;
  return [r, g, b];
};

const _rgbToHex = (rgb: readonly [number, number, number]): string => {
  const toByte = (v: number): string => {
    const clamped = Math.max(0, Math.min(1, v));
    return Math.round(clamped * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${toByte(rgb[0])}${toByte(rgb[1])}${toByte(rgb[2])}`;
};

const _srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

const _linearToSrgb = (c: number): number => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

interface OklabTriplet {
  a: number;
  b: number;
  l: number;
}

const _linearRgbToOklab = (r: number, g: number, b: number): OklabTriplet => {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
    l: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  };
};

const _oklabToLinearRgb = (lab: OklabTriplet): [number, number, number] => {
  const l_ = lab.l + 0.3963377774 * lab.a + 0.2158037573 * lab.b;
  const m_ = lab.l - 0.1055613458 * lab.a - 0.0638541728 * lab.b;
  const s_ = lab.l - 0.0894841775 * lab.a - 1.291485548 * lab.b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  ];
};

const _oklabToOklch = (lab: OklabTriplet): OklchTriplet => {
  const c = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  let h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { c, h, l: lab.l };
};

const _oklchToOklab = (oklch: OklchTriplet): OklabTriplet => {
  const rad = (oklch.h * Math.PI) / 180;
  return { a: oklch.c * Math.cos(rad), b: oklch.c * Math.sin(rad), l: oklch.l };
};
